// fw Field / Input / Select / Textarea.
//
// 48px minimum height and a 16px font on every control: below 16px iOS Safari
// zooms the page on focus and does not zoom back, which is the "controls went
// off the screen" report. Exported from the barrel as FwField, FwInput,
// FwSelect, FwTextarea so they do not collide with the old ui primitives.

import React, { useId } from 'react';
import { Icon } from '../../components/Icon.js';
import './Field.css';

/**
 * Field — label + control + hint/error. Wires htmlFor, aria-invalid and
 * aria-describedby onto the single child control.
 */
export function Field({ label, hint, error, htmlFor, required, children, className = '', ...rest }) {
  const auto = useId();
  const id = htmlFor || `fw-field-${auto}`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const wired = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child, {
      id: child.props.id || id,
      'aria-invalid': error ? 'true' : child.props['aria-invalid'],
      'aria-describedby': error ? errorId : (hint ? hintId : child.props['aria-describedby']),
    });
  });
  return (
    <div className={['fw-field', error ? 'fw-field--error' : '', className].filter(Boolean).join(' ')} {...rest}>
      {label ? (
        <label htmlFor={id} className="fw-field__label">
          {label}
          {required ? <span className="fw-field__required" aria-hidden="true"> *</span> : null}
        </label>
      ) : null}
      {wired}
      {error ? <div id={errorId} className="fw-field__error" role="alert">{error}</div> : null}
      {!error && hint ? <div id={hintId} className="fw-field__hint">{hint}</div> : null}
    </div>
  );
}

export const Input = React.forwardRef(function Input({ className = '', ...rest }, ref) {
  return <input ref={ref} className={['fw-input', className].filter(Boolean).join(' ')} {...rest} />;
});

export const Textarea = React.forwardRef(function Textarea({ rows = 4, className = '', ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={['fw-textarea', className].filter(Boolean).join(' ')} {...rest} />;
});

export const Select = React.forwardRef(function Select({ children, className = '', ...rest }, ref) {
  return (
    <span className="fw-select-wrap">
      <select ref={ref} className={['fw-select', className].filter(Boolean).join(' ')} {...rest}>
        {children}
      </select>
      <span className="fw-select__chevron" aria-hidden="true"><Icon name="chevronDown" /></span>
    </span>
  );
});

export default Field;
